import React, { useState, useEffect } from 'react';
import { translations } from '../data/translations';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const formatTemplateString = (template, variables) => {
    if (typeof template !== 'string') {
        return '';
    }
    return template.replace(/\{(\w+)\}/g, (match, key) => {
        if (Object.prototype.hasOwnProperty.call(variables, key)) {
            return variables[key];
        }
        return match;
    });
};

const adjustHexColor = (hexColor, delta) => {
    if (!hexColor || typeof hexColor !== 'string') {
        return '#2c3e50';
    }

    let color = hexColor.replace('#', '');
    if (color.length === 3) {
        color = color
            .split('')
            .map((char) => char + char)
            .join('');
    }

    const numeric = parseInt(color, 16);
    if (Number.isNaN(numeric)) {
        return hexColor;
    }

    const r = clamp(((numeric >> 16) & 0xff) + delta, 0, 255);
    const g = clamp(((numeric >> 8) & 0xff) + delta, 0, 255);
    const b = clamp((numeric & 0xff) + delta, 0, 255);

    const toHex = (component) => component.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const KegelTimer = ({ settings, language, onComplete, onBackgroundChange, onRunStateChange }) => {
    const { contractionTime, relaxationTime, iterations, contractionColor, relaxationColor } = settings;

    const [isRunning, setIsRunning] = useState(false);
    const [currentPhase, setCurrentPhase] = useState('relaxation');
    const [timeLeft, setTimeLeft] = useState(contractionTime);
    const [currentIteration, setCurrentIteration] = useState(1);

    useEffect(() => {
        let timer;

        if (isRunning && timeLeft > 0) {
            timer = setTimeout(() => setTimeLeft((prev) => prev - 1), 1000);
        } else if (isRunning && timeLeft === 0) {
            if (currentPhase === 'contraction') {
                setCurrentPhase('relaxation');
                setTimeLeft(relaxationTime);
            } else {
                if (currentIteration >= iterations) {
                setIsRunning(false);
                if (typeof onRunStateChange === 'function') {
                    onRunStateChange(false);
                }
                if (onComplete) {
                    onComplete();
                }
                return;
            }

            setCurrentIteration((prev) => prev + 1);
            setCurrentPhase('contraction');
            setTimeLeft(contractionTime);
        }
    }

    return () => clearTimeout(timer);
}, [isRunning, timeLeft, currentPhase, currentIteration, contractionTime, relaxationTime, iterations, onComplete, onRunStateChange]);

    useEffect(() => {
        if (!isRunning) {
            setTimeLeft(contractionTime);
        }
    }, [contractionTime, isRunning]);

    useEffect(() => {
        if (!onBackgroundChange) {
            return;
        }

        if (isRunning) {
            const activeColor = currentPhase === 'contraction' ? contractionColor : relaxationColor;
            onBackgroundChange(activeColor);
        } else {
            onBackgroundChange(null);
        }
    }, [isRunning, currentPhase, contractionColor, relaxationColor, onBackgroundChange]);

    useEffect(() => {
        return () => {
            if (onBackgroundChange) {
                onBackgroundChange(null);
            }
        };
    }, [onBackgroundChange]);

    const phaseDuration = currentPhase === 'contraction' ? contractionTime : relaxationTime;
    const activeColor = currentPhase === 'contraction' ? contractionColor : relaxationColor;
    const progressRatio = phaseDuration > 0 ? (phaseDuration - timeLeft) / phaseDuration : 0;
    const progressDegrees = clamp(progressRatio * 360, 0, 360);
    const timerTrackColor = 'rgba(44, 62, 80, 0.12)';
    const timerGradient = `conic-gradient(${activeColor} 0deg ${progressDegrees}deg, ${timerTrackColor} ${progressDegrees}deg 360deg)`;
    const phaseLetterSpacing = currentPhase === 'contraction' ? '0.08em' : '0.42em';
    const phaseTextColor =
        currentPhase === 'contraction'
            ? adjustHexColor(contractionColor, -60)
            : adjustHexColor(relaxationColor, 80);
    const timerValueColor =
        currentPhase === 'contraction'
            ? adjustHexColor(contractionColor, -70)
            : adjustHexColor(relaxationColor, 90);
    const timerInnerGlow =
        currentPhase === 'contraction'
            ? adjustHexColor(contractionColor, -30)
            : adjustHexColor(relaxationColor, 40);

    const phaseIndicatorStyles = {
        letterSpacing: phaseLetterSpacing,
        color: phaseTextColor
    };

    const timerCircleStyles = {
        background: timerGradient,
        boxShadow: `0 22px 40px ${activeColor}33`
    };

    const timerInnerStyles = {
        borderColor: `${timerInnerGlow}55`
    };

    const timerValueStyles = {
        color: timerValueColor
    };

    const isFinalRelaxationPhase = currentPhase === 'relaxation' && currentIteration === iterations;
    const nextPhaseKey = currentPhase === 'contraction' ? 'relaxation' : 'contraction';
    const nextPhaseLabel = isFinalRelaxationPhase
        ? translations[language].completed
        : translations[language][nextPhaseKey];
    const nextPhaseAnnouncement = `${translations[language].nextPhaseIn} ${timeLeft}s: ${nextPhaseLabel}`;
    const programSummaryTemplate = translations[language].programSummary;
    const programSummaryText =
        programSummaryTemplate && programSummaryTemplate.includes('{')
            ? formatTemplateString(programSummaryTemplate, {
                iterations,
                contractionTime,
                relaxationTime
            })
            : programSummaryTemplate ||
              `Program: ${iterations} cycles (${contractionTime}s contraction / ${relaxationTime}s relaxation)`;

    const startExercise = () => {
        setIsRunning(true);
        if (typeof onRunStateChange === 'function') {
            onRunStateChange(true);
        }
        setCurrentPhase('contraction');
        setTimeLeft(contractionTime);
        setCurrentIteration(1);
    };

    const stopExercise = () => {
        setIsRunning(false);
        if (typeof onRunStateChange === 'function') {
            onRunStateChange(false);
        }
    };

    const getPhaseText = () => {
        return currentPhase === 'contraction'
            ? translations[language].contraction
            : translations[language].relaxation;
    };

    return (
        <div className="kegel-container">
            <div className="kegel-overlay">
                {!isRunning ? (
                    <>
                        <h2>{translations[language].appTitle}</h2>
                        <p className="timer-program-meta">{programSummaryText}</p>
                        <button
                            className="control-button control-button--start"
                            onClick={startExercise}
                            title={translations[language].start}
                        >
                            <span aria-hidden="true" className="control-button__icon control-button__icon--play" />
                            <span className="sr-only">{translations[language].start}</span>
                        </button>
                    </>
                ) : (
                    <>
                        <div className="phase-indicator" style={phaseIndicatorStyles} role="status" aria-live="polite">
                            {getPhaseText()}
                        </div>

                        <div className="timer-circle" style={timerCircleStyles}>
                          <div
                            className="timer-circle__inner"
                            style={timerInnerStyles}
                            role="timer"
                            aria-live="assertive"
                            aria-label={nextPhaseAnnouncement}
                          >
                            <span className="timer-circle__value" style={timerValueStyles}>
                              {timeLeft}s
                            </span>
                          </div>
                        </div>
                        <button
                          className="control-button control-button--stop"
                          onClick={stopExercise}
                          title={translations[language].stop}
                        >
                            <span aria-hidden="true" className="control-button__icon control-button__icon--pause" />
                            <span className="sr-only">{translations[language].stop}</span>
                        </button>

                        <div className="progress-info" aria-live="polite">
                            <div>
                                {currentIteration} / {iterations}
                            </div>

                            <div className="progress-dots">
                                {Array.from({ length: iterations }, (_, index) => (
                                    <div
                                        key={index}
                                        className={`progress-dot ${index < currentIteration - 1 ? 'completed' : ''} ${index === currentIteration - 1 ? 'current' : ''
                                            }`}
                                        title={`Cycle ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default KegelTimer;

